import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Wrench } from "lucide-react";
import { ModalSectionLayout } from "./ModalSectionLayout";
import { Button } from "@/components/shadcn/button";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { SpotToast } from "@/components/SpotToast";
import { MCPToolRow } from "@/components/MCP/MCPToolRow";
import apiClient from "@/services/api-client";
import { DATA_KEYS } from "@/utils/useRemoteData";
import type { ActiveCredential } from "./types";
import { enabledToolNames, type MCPStateMeta } from "./mcpToolsState";

interface ToolsResponse {
  credential_id: string;
  enabled_tools: string[];
  discovered_tools: string[];
  tool_list_hash: string;
}

function readMcpState(cred: ActiveCredential): MCPStateMeta | null {
  const meta = cred.meta;
  if (!meta || typeof meta !== "object") return null;
  const mcp = (meta as Record<string, unknown>)["mcp"];
  if (!mcp || typeof mcp !== "object") return null;
  return mcp as unknown as MCPStateMeta;
}

export function MCPToolsPanel({
  cred,
  groupId,
  onUpdated,
}: {
  cred: ActiveCredential;
  groupId: string;
  onUpdated: () => void;
}) {
  const state = readMcpState(cred);
  const queryClient = useQueryClient();
  const serverEnabled = useMemo(
    () => (state ? enabledToolNames(state) : []),
    [state]
  );
  // Optimistic mirror of the server's enabled set. The Switch reads from this
  // so the UI flips instantly on click; we sync it to the server payload
  // whenever the cred prop changes (mutation success → query refetch →
  // parent passes a fresh cred down). On error we roll back here too.
  const [optimisticEnabled, setOptimisticEnabled] = useState<Set<string>>(
    () => new Set(serverEnabled)
  );
  useEffect(() => {
    setOptimisticEnabled(new Set(serverEnabled));
  }, [serverEnabled]);

  const setEnabledMutation = useMutation({
    mutationFn: async (enabledTools: string[]) => {
      const resp = await apiClient.patch<ToolsResponse>(
        `/api/backend/groups/${groupId}/credentials/${cred.credentialId}/mcp/tools`,
        { enabled_tools: enabledTools }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onUpdated();
    },
    onError: (err) => {
      // Roll back the optimistic flip to the server's authoritative set.
      setOptimisticEnabled(new Set(serverEnabled));
      SpotToast.error(
        err instanceof Error ? err.message : "Failed to update tools"
      );
    },
  });

  const reprobeMutation = useMutation({
    mutationFn: async () => {
      const resp = await apiClient.post<ToolsResponse>(
        `/api/backend/groups/${groupId}/credentials/${cred.credentialId}/mcp/reprobe`,
        {}
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onUpdated();
      SpotToast.success("Tools refreshed");
    },
    onError: (err) => {
      SpotToast.error(err instanceof Error ? err.message : "Probe failed");
    },
  });

  // ``enabled`` reads from the optimistic mirror so toggling feels instant.
  const enabled = optimisticEnabled;

  if (!state) {
    return (
      <ModalSectionLayout
        title="Tools"
        description="Tools exposed by this MCP server."
      >
        <NoxusEmptyBlock
          icon={Wrench}
          title="Not probed yet"
          description="Reconnect to discover tools from the server."
        />
      </ModalSectionLayout>
    );
  }

  const toggle = (name: string) => {
    const next = new Set(enabled);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    // Flip the UI immediately; the mutation will either confirm (no visible
    // change) or fail and roll back via ``onError`` above. Each request
    // sends the full ``enabled_tools`` list so concurrent toggles are
    // last-write-wins on the server — fine since the optimistic Set is
    // the snapshot the user actually sees.
    setOptimisticEnabled(next);
    setEnabledMutation.mutate(Array.from(next));
  };

  return (
    <ModalSectionLayout
      title="Tools"
      description="Choose which tools from this MCP server are available in the workspace."
      footer={
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reprobeMutation.mutate()}
            disabled={reprobeMutation.isPending}
          >
            <RefreshCw
              size={14}
              className={reprobeMutation.isPending ? "animate-spin" : ""}
            />
            Refresh tools
          </Button>
        </div>
      }
    >
      {state.discovered_tools.length === 0 ? (
        <NoxusEmptyBlock
          icon={Wrench}
          title="No tools"
          description="The server did not report any tools."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {state.discovered_tools.map((tool) => (
            <MCPToolRow
              key={tool.name}
              tool={tool}
              integrationImage={cred.integration.image}
              integrationName={cred.integration.display_name}
              checked={enabled.has(tool.name)}
              onToggle={() => toggle(tool.name)}
            />
          ))}
        </div>
      )}
    </ModalSectionLayout>
  );
}
